import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./HeaderSection.module.scss";
import { EntryPointsIcon } from "@/frontend/shared/assets/icons";
import { LinkedInLettersIcon } from "@/frontend/shared/assets/icons/LinkedInLettersIcon";
import { useState } from "react";
import clsx from "clsx";
import { refactorWebsiteLink } from "@/frontend/shared/lib/refactorWebsiteLink/refactorWebsiteLink";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { createPortfolioCompanies } from "@/frontend/pages/CompaniesPage/model/services/createPortfolioCompanies";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { AddCompanyTagsModal } from "@/frontend/components/AddCompanyTagsModal/ui/AddCompanyTagsModal";
import { fireAnalyticsEvent } from "@/frontend/features/analytics";
import { EPostHogEventName } from "@/frontend/features/analytics/const/const";
import { TagTabV2 } from "@/frontend/shared/ui/TagTabV2";
import { Tag } from "@/frontend/shared/ui/TagV2";
import { useMe } from "@/frontend/shared/lib/hooks/useMe";
import { useOrgPhoto } from "@/frontend/shared/lib/hooks/useOrgPhoto";
import { WebIcon } from "@/frontend/shared/assets/icons/WebIcon";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import { CommentIcon } from "@/frontend/shared/assets/icons/CommentIcon";
import { EActiveView, TCompanyProfile } from "@/frontend/features/Company/model/types/types";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { AddCompaniesToPipelineModal } from "@/frontend/components/AddCompaniesToPipelineModal/AddCompaniesToPipelineModal";
import { patchBulkAssignTags } from "@/frontend/features/Company/model/patchBulkAssignTags";
import { removeTagFromCompany } from "@/frontend/features/Company/model/removeTagFromCompany";
import { AddTagsModalV2 } from "@/frontend/components/AddTagsModalV2/ui/AddTagsModalV2";

export function HeaderSection({
  data,
  activeView,
  onChangeActiveView,
  companyTags,
  companyId,
  triggerRefetch,
  refetchDetailsData,
  isContainerScrolled,
  isFounder,
  isExpert,
  setIsCommentsSectionOpen,
  showComments
}: {
  data?: TCompanyProfile;
  activeView: EActiveView;
  onChangeActiveView: (view: EActiveView) => void;
  companyTags?: { id: number; name: string }[];
  companyId?: number;
  triggerRefetch: () => void;
  refetchDetailsData: () => void;
  isContainerScrolled?: boolean;
  isFounder?: boolean;
  isExpert?: boolean;
  setIsCommentsSectionOpen?: (value: boolean) => void;
  showComments?: boolean;
}) {
  if (!data)
    return (
      <div className={styles.container}>
        <div className={styles.loaderContainer}>
          <Loader />
        </div>
      </div>
    );

  const {
    name,
    website,
    logoUrl,
    city,
    country,
    description,
    githubUrl,
    headline,
    linkedInUrl,
    productHuntUrl,
    state,
    twitterUrl,
    totalFunding,
    harmonicTags
  } = data;

  const { data: profileData } = useMe();
  const organizationImageUrl = useOrgPhoto(profileData?.organizationId);

  const isMobile = useScreenWidth() <= 768;

  const [isAddTagsModalOpen, setIsAddTagsModalOpen] = useState(false);
  const [isAddToPipelineModalOpen, setIsAddToPipelineModalOpen] = useState(false);

  function displayedWebsiteField() {
    const content = website ? (
      <div className={styles.websiteContainerBlock}>
        <NormalButton variant="secondary" size="small" className={styles.websiteButton} color="greyscale700">
          <a href={refactorWebsiteLink(website)} target="_blank" rel="noreferrer" className={styles.websiteContainer}>
            <WebIcon />
            <Typography variant="label-5" color="greyscale700">
              {website}
            </Typography>
          </a>
        </NormalButton>
      </div>
    ) : null;

    return content;
  }

  const addTagsFn = async (id: number, tags: string[]) => {
    await patchBulkAssignTags([id], tags);
  };

  const removeTagsFn = async (id: number, tagIds: number[]) => {
    await removeTagFromCompany({ tagIds, companyId: id });
  };

  return (
    <div className={styles.container}>
      <div className={styles.block}>
        <div className={clsx(styles.upperContent, !isContainerScrolled && styles.minimizedUpperContent)}>
          <div className={styles.logoContainer}>
            {logoUrl ? (
              <img src={logoUrl} alt={name} className={styles.logo} />
            ) : (
              <div className={styles.logoPlaceholder} />
            )}
            {!isFounder && isMobile && (
              <>
                <NormalButton
                  size="large"
                  variant="secondary"
                  className={styles.inviteButton}
                  onClick={() => setIsAddToPipelineModalOpen(true)}
                >
                  <EntryPointsIcon />
                  Add {isMobile ? "" : "to Pipeline"}
                </NormalButton>
                <AddCompaniesToPipelineModal
                  ids={[companyId || 0]}
                  triggerRefetch={triggerRefetch}
                  isOpen={isAddToPipelineModalOpen}
                  onClose={() => setIsAddToPipelineModalOpen(false)}
                />
              </>
            )}
          </div>
          <div className={styles.infoContainer}>
            <div className={styles.titleContainer}>
              <div className={styles.title}>
                <Typography variant="label-1" color="greyscale900">
                  {name}
                </Typography>
              </div>
              <div className={styles.info}>
                {displayedWebsiteField()}
                <div className={styles.iconsContainer}>
                  {linkedInUrl && (
                    <a href={linkedInUrl} target="_blank" rel="noreferrer" className={styles.linkedinIconContainer}>
                      <LinkedInLettersIcon className={styles.linkedinIcon} />
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div
              className={clsx(styles.descriptionBlock, !isContainerScrolled && isMobile && styles.hiddenDescription)}
            >
              <div className={styles.subDescription}>
                <Typography variant="body-2" color="greyscale800" className={styles.shortDescription}>
                  {headline}
                </Typography>
              </div>
            </div>
          </div>
          {!isFounder && !isMobile && (
            <>
              <NormalButton
                size="large"
                variant="secondary"
                className={styles.inviteButton}
                onClick={() => setIsAddToPipelineModalOpen(true)}
              >
                <EntryPointsIcon />
                Add to Pipeline
              </NormalButton>
              <AddCompaniesToPipelineModal
                ids={[companyId || 0]}
                triggerRefetch={triggerRefetch}
                isOpen={isAddToPipelineModalOpen}
                onClose={() => setIsAddToPipelineModalOpen(false)}
              />
            </>
          )}
        </div>
        <div className={clsx(styles.tagsContainerBlock, !isContainerScrolled && styles.hiddenTagsContainer)}>
          <div className={clsx(styles.tagsContainer)}>
            {!isFounder && (
              <div className={styles.customTagsContainer}>
                <TagTabV2
                  customTags={true}
                  addTagClick={() => setIsAddTagsModalOpen(true)}
                  logoSrc={organizationImageUrl}
                >
                  {companyTags?.map((tag: { name: string; id: number }, index: number) => {
                    return <Tag children={tag.name} type="custom" key={index} className={styles.whiteTag} />;
                  })}
                </TagTabV2>
              </div>
            )}
            {!!harmonicTags && harmonicTags.length > 0 && (
              <div className={styles.innoveraTagsContainer}>
                <TagTabV2 customTags={false}>
                  {harmonicTags?.map((tag: string, index: number) => {
                    return <Tag children={tag} type="innovera" key={index} className={styles.tag} />;
                  })}
                </TagTabV2>
              </div>
            )}
            <AddTagsModalV2
              id={companyId || 0}
              triggerRefetch={refetchDetailsData}
              isOpen={isAddTagsModalOpen}
              onClose={() => setIsAddTagsModalOpen(false)}
              addTagsFn={addTagsFn}
              removeTagsFn={removeTagsFn}
              currentTags={companyTags?.map((tag) => tag.name) || []}
            />
          </div>
        </div>
        <div className={styles.bottomContent}>
          <div className={styles.viewSwitcherContainer}>
            <Typography
              variant="label-4"
              color="greyscale500"
              onClick={() => onChangeActiveView(EActiveView.PROFILE)}
              className={clsx(
                styles.option,
                (activeView === EActiveView.PROFILE || (isFounder && !isExpert)) && styles.active
              )}
            >
              Profile
            </Typography>
            {!isFounder && (
              <Typography
                variant="label-4"
                color="greyscale500"
                onClick={() => onChangeActiveView(EActiveView.DATA_ROOM)}
                className={clsx(styles.option, activeView === EActiveView.DATA_ROOM && styles.active)}
              >
                Data Room
              </Typography>
            )}
            {(!isFounder || isExpert) && (
              <Typography
                variant="label-4"
                color="greyscale500"
                onClick={() => onChangeActiveView(EActiveView.ANNOTATIONS)}
                className={clsx(styles.option, activeView === EActiveView.ANNOTATIONS && styles.active)}
              >
                Notes
              </Typography>
            )}
          </div>
          {!showComments && !!setIsCommentsSectionOpen && (
            <NormalButton variant="tertiary" size="large" onClick={() => setIsCommentsSectionOpen(true)}>
              <CommentIcon />
            </NormalButton>
          )}
        </div>
      </div>
    </div>
  );
}
